'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#160604] pt-16 pb-8 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2.5">
              <AppImage
                src="/assets/images/Untitled_design__7_-1782403998447.png"
                alt="Roetfilter Center logo"
                width={44}
                height={44}
                className="h-10 w-auto object-contain"
              />
              <span className="font-black text-white text-lg">
                Roetfilter<span className="text-accent"> Center</span>
              </span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-sm">
              Enige erkende roetfilterspecialist in Vlaanderen met reiniging, revisie, diagnose & montage onder één dak. 12 jaar ervaring. 1 jaar garantie.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Icon name="MapPinIcon" size={14} className="text-accent shrink-0" />
                <span>Dijkstraat 22 – 9160 Lokeren</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Icon name="PhoneIcon" size={14} className="text-accent shrink-0" variant="solid" />
                <a href="tel:0498545474" onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion('tel:0498545474'); }} className="hover:text-accent transition-colors">0498 54 54 74</a>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Icon name="EnvelopeIcon" size={14} className="text-accent shrink-0" />
                <a href="mailto:info@roetfilter-center.be" className="hover:text-accent transition-colors">info@roetfilter-center.be</a>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Icon name="ClockIcon" size={14} className="text-accent shrink-0" />
                <span>Ma–Vr: 08:00–18:30 | Za: 09:00–17:30 | Zon: op afspraak</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <a
                href="https://wa.me/32498545474"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-green-500 transition-colors"
              >
                WhatsApp
              </a>
              <a
                href="https://www.google.com/maps?q=Dijkstraat+22,+9160+Lokeren"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-xs font-bold hover:bg-white/20 transition-colors border border-white/20"
              >
                Google Maps
              </a>
            </div>
          </div>

          {/* Diensten */}
          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-accent">Diensten</p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Roetfilter Diagnose', href: '/roetfilter-diagnose' },
                { label: 'Roetfilter Reinigen', href: '/roetfilter-reinigen' },
                { label: 'Roetfilter Revisie', href: '/roetfilter-revisie' },
                { label: 'Roetfilter Vervangen', href: '/roetfilter-vervangen' },
                { label: 'Roetfilter Verwijderen', href: '/roetfilter-verwijderen' },
                { label: 'Katalysator Problemen', href: '/katalysator-problemen' },
                { label: 'EGR-klep Problemen', href: '/egr-klep-problemen' },
                { label: 'AdBlue Problemen', href: '/adblue-problemen' },
                { label: 'Bestelwagen Roetfilter', href: '/bestelwagen-dpf' },
                { label: 'Bestelwagen Roetfilter Reinigen', href: '/roetfilter-reinigen-bestelwagen' },
              ]?.map((s) => (
                <Link key={s?.href} href={s?.href} className="text-sm font-medium text-white/70 hover:text-accent transition-colors">
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Merken */}
          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-accent">Per Merk</p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Audi', href: '/audi-roetfilter' },
                { label: 'BMW', href: '/bmw-roetfilter' },
                { label: 'Citroën', href: '/citroen-roetfilter' },
                { label: 'Mercedes', href: '/mercedes-roetfilter' },
                { label: 'Peugeot', href: '/peugeot-roetfilter' },
                { label: 'Renault', href: '/renault-roetfilter' },
                { label: 'Volkswagen', href: '/volkswagen-roetfilter' },
                { label: 'Volvo', href: '/volvo-roetfilter' },
                { label: 'Alle merken →', href: '/diensten' },
              ]?.map((s) => (
                <Link key={s?.href} href={s?.href} className="text-sm font-medium text-white/70 hover:text-accent transition-colors">
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Info & Locaties */}
          <div className="space-y-3">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-accent">Info & Locaties</p>
            <div className="flex flex-col gap-2">
              {[
                { label: 'Over Ons', href: '/over-roetfilter-center' },
                { label: 'Roetfilter Specialist', href: '/roetfilter-specialist' },
                { label: 'Veelgestelde Vragen', href: '/veelgestelde-vragen' },
                { label: 'Afgekeurd voor Roet', href: '/afgekeurd-voor-roet' },
                { label: 'Contact', href: '/contact' },
                { label: 'Offerte Aanvragen', href: '/offerte' },
                { label: 'Onze Locatie', href: '/locatie' },
                { label: 'Alle Locaties', href: '/locaties' },
                { label: 'Gent', href: '/roetfilter-reinigen-gent' },
                { label: 'Antwerpen', href: '/locaties/antwerpen' },
                { label: 'Brussel', href: '/locaties/brussel' },
                { label: 'Sint-Niklaas', href: '/roetfilter-reinigen-sint-niklaas' },
                { label: 'Mechelen', href: '/locaties/mechelen' },
                { label: 'Dendermonde', href: '/roetfilter-reinigen-dendermonde' },
              ]?.map((l) => (
                <Link key={l?.href} href={l?.href} className="text-sm font-medium text-white/70 hover:text-accent transition-colors">
                  {l?.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/40">
            © 2026 Roetfilter Center – Specialist in Diagnose &amp; Herstelling
          </p>
          <div className="flex items-center gap-4 text-sm text-white/40">
            <Link href="/veelgestelde-vragen" className="hover:text-white/70 transition-colors">FAQ</Link>
            <Link href="/contact" className="hover:text-white/70 transition-colors">Contact</Link>
            <Link href="/offerte" className="hover:text-white/70 transition-colors">Offerte</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
